import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import "./acceptance.css";

import {
  AppsOutlined,
  CalendarMonthOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  FilterAltOutlined,
  HourglassTopOutlined,
  LaunchOutlined,
  PaymentsOutlined,
  RefreshOutlined,
  SearchOutlined,
  TableRowsOutlined,
  VerifiedOutlined,
  CancelOutlined,
  ReceiptLongOutlined,
  GroupsOutlined,
} from "@mui/icons-material";

const API = process.env.REACT_APP_SERVER_SCRIPT_HOST;

const MONTHS = [
  { value: "Jan", label: "January" },
  { value: "Feb", label: "February" },
  { value: "Mar", label: "March" },
  { value: "Apr", label: "April" },
  { value: "May", label: "May" },
  { value: "Jun", label: "June" },
  { value: "Jul", label: "July" },
  { value: "Aug", label: "August" },
  { value: "Sep", label: "September" },
  { value: "Oct", label: "October" },
  { value: "Nov", label: "November" },
  { value: "Dec", label: "December" },
];

const getName = (item) =>
  [item?.first_Name, item?.sur_Name, item?.other_Name]
    .filter(Boolean)
    .join(" ") || "Unnamed Applicant";

const getInitials = (item) => {
  const name = getName(item);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

const getProgramme = (item) =>
  item?.course_of_study ||
  item?.course_of_study ||
  "Programme not specified";

const getAcceptance = (item) =>
  item?.admissions?.acceptance_of_adm || "Pending";

const getPaymentStatus = (item) =>
  item?.account_status?.paymentStatus || "Pending";

const getReference = (item) =>
  item?.account_status?.reference_number || "Not assigned";

const getInvoice = (item) =>
  item?.account_status?.transaction_id || "Not assigned";

const getDate = (item) =>
  item?.registered_date?.date ||
  item?.registered_date ||
  item?.createdAt ||
  "—";

const normalize = (value) =>
  String(value || "pending")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

const formatDate = (value) => {
  if (!value || value === "—") return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ApplicantAvatar({ applicant, large = false }) {
  return (
    <div
      className={`accept-avatar ${
        large ? "accept-avatar--large" : ""
      }`}
    >
      {applicant?.passport?.url ? (
        <img
          src={applicant.passport.url}
          alt={getName(applicant)}
        />
      ) : (
        <span>{getInitials(applicant)}</span>
      )}
    </div>
  );
}

function AcceptanceStatus({ value }) {
  const normalized = normalize(value);

  let label = value || "Pending";

  if (normalized === "accepted") label = "Accepted";
  if (normalized === "rejected") label = "Rejected";
  if (normalized === "pending") label = "Pending";

  const icon =
    normalized === "accepted" ? (
      <CheckCircleOutlined />
    ) : normalized === "rejected" ? (
      <CancelOutlined />
    ) : (
      <HourglassTopOutlined />
    );

  return (
    <span
      className={`accept-status accept-status--${normalized}`}
    >
      {icon}
      {label}
    </span>
  );
}

function PaymentStatus({ value }) {
  const normalized = normalize(value);

  const paid =
    normalized === "success" ||
    normalized === "successful" ||
    normalized === "paid";

  const failed = normalized === "failed";

  return (
    <span
      className={`accept-payment ${
        paid
          ? "accept-payment--paid"
          : failed
          ? "accept-payment--failed"
          : "accept-payment--pending"
      }`}
    >
      <span />
      {paid ? "Paid" : failed ? "Failed" : "Pending"}
    </span>
  );
}

function MetricCard({
  icon,
  value,
  label,
  description,
  variant,
}) {
  return (
    <article
      className={`accept-metric accept-metric--${variant}`}
    >
      <div className="accept-metric__top">
        <div className="accept-metric__icon">{icon}</div>

        <span className="accept-metric__live">
          <span />
          LIVE
        </span>
      </div>

      <strong>{value ?? 0}</strong>

      <h3>{label}</h3>

      <p>{description}</p>
    </article>
  );
}

function TableView({ applicants }) {
  return (
    <section className="accept-table-card">
      <div className="accept-table-card__header">
        <div>
          <span className="accept-eyebrow">
            ACCEPTANCE WORKSPACE
          </span>

          <h2>Admission acceptance records</h2>

          <p>
            Monitor accepted, pending and rejected admission
            responses.
          </p>
        </div>

        <div className="accept-record-count">
          <strong>{applicants.length}</strong>
          <span>records</span>
        </div>
      </div>

      <div className="accept-table-scroll">
        <table className="accept-table">
          <thead>
            <tr>
              <th className="accept-sn">#</th>
              <th>Applicant</th>
              <th>Application</th>
              <th>Gender</th>
              <th>Invoice No.</th>
              <th>Reference No.</th>
              <th>Payment</th>
              <th>Acceptance</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {applicants.map((applicant, index) => (
              <tr
                key={
                  applicant?._id ||
                  applicant?.application_number ||
                  index
                }
              >
                <td className="accept-sn">
                  {String(index + 1).padStart(2, "0")}
                </td>

                <td>
                  <div className="accept-applicant">
                    <ApplicantAvatar applicant={applicant} />

                    <div>
                      <strong>{getName(applicant)}</strong>

                      <span>
                        {getProgramme(applicant)}
                      </span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="accept-application-id">
                    {applicant?.application_number || "N/A"}
                  </span>
                </td>

                <td>
                  <span className="accept-gender">
                    {applicant?.gender || "—"}
                  </span>
                </td>

                <td>
                  <span className="accept-reference">
                    <ReceiptLongOutlined />
                    {getInvoice(applicant)}
                  </span>
                </td>

                <td>
                  <span className="accept-reference">
                    {getReference(applicant)}
                  </span>
                </td>

                <td>
                  <PaymentStatus
                    value={getPaymentStatus(applicant)}
                  />
                </td>

                <td>
                  <AcceptanceStatus
                    value={getAcceptance(applicant)}
                  />
                </td>

                <td>
                  <div className="accept-date">
                    <CalendarMonthOutlined />
                    {formatDate(getDate(applicant))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CardView({ applicants }) {
  return (
    <section className="accept-grid">
      {applicants.map((applicant, index) => (
        <article
          className="accept-candidate-card"
          key={
            applicant?._id ||
            applicant?.application_number ||
            index
          }
        >
          <div className="accept-card__top">
            <div className="accept-card__identity">
              <ApplicantAvatar
                applicant={applicant}
                large
              />

              <div>
                <h3>{getName(applicant)}</h3>

                <span>
                  {applicant?.application_number ||
                    "No application number"}
                </span>
              </div>
            </div>

            <AcceptanceStatus
              value={getAcceptance(applicant)}
            />
          </div>

          <div className="accept-card__programme">
            <div className="accept-card__programme-icon">
              <VerifiedOutlined />
            </div>

            <div>
              <span>ADMISSION PROGRAMME</span>
              <strong>{getProgramme(applicant)}</strong>
            </div>
          </div>

          <div className="accept-card__status-panel">
            <div>
              <span>Payment</span>
              <PaymentStatus
                value={getPaymentStatus(applicant)}
              />
            </div>

            <div>
              <span>Acceptance</span>
              <AcceptanceStatus
                value={getAcceptance(applicant)}
              />
            </div>
          </div>

          <div className="accept-card__details">
            <div>
              <span>Invoice</span>
              <strong>{getInvoice(applicant)}</strong>
            </div>

            <div>
              <span>Reference</span>
              <strong>{getReference(applicant)}</strong>
            </div>

            <div>
              <span>Submitted</span>
              <strong>{formatDate(getDate(applicant))}</strong>
            </div>
          </div>

          <div className="accept-card__footer">
            <span>
              <GroupsOutlined />
              Admission acceptance
            </span>

            <LaunchOutlined />
          </div>
        </article>
      ))}
    </section>
  );
}

function EmptyState({ filtered, clearFilters }) {
  return (
    <section className="accept-empty">
      <div className="accept-empty__icon">
        <SearchOutlined />
      </div>

      <h3>
        {filtered
          ? "No matching acceptance records"
          : "No acceptance records"}
      </h3>

      <p>
        {filtered
          ? "Try another search or clear the active month filter."
          : "Admission acceptance records will appear here."}
      </p>

      {filtered && (
        <button
          type="button"
          onClick={clearFilters}
        >
          <CloseOutlined />
          Clear filters
        </button>
      )}
    </section>
  );
}

export default function Acceptance() {
  const [loading, setLoading] = useState(false);

  const [applicants, setApplicants] = useState([]);

  const [counts, setCounts] = useState({
    count: 0,
    pendings: 0,
    accepteds: 0,
    rejecteds: 0,
  });

  const [changeview, setChangeView] = useState("table");
  const [month, setMonth] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const applyResponse = (response) => {
    const result = response?.data || {};

    setApplicants(result?.response || []);

    setCounts({
      count: result?.count || 0,
      pendings: result?.pendings || 0,
      accepteds: result?.accepteds || 0,
      rejecteds: result?.rejecteds || 0,
    });
  };

  const getApplicants = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API}/acceptance`
      );

      applyResponse(response);
    } catch (error) {
      console.error(
        "Unable to load acceptance records:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const getByMonths = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${API}/acceptance`,
        { month }
      );

      applyResponse(response);
    } catch (error) {
      console.error(
        "Unable to filter acceptance records:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getApplicants();
  }, []);

  const visibleApplicants = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return applicants;

    return applicants.filter((applicant) => {
      const searchable = [
        getName(applicant),
        applicant?.application_number,
        applicant?.gender,
        getProgramme(applicant),
        getInvoice(applicant),
        getReference(applicant),
        getPaymentStatus(applicant),
        getAcceptance(applicant),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [applicants, searchTerm]);

  const clearFilters = () => {
    setMonth("");
    setSearchTerm("");
    getApplicants();
  };

  const hasFilters =
    Boolean(month) || Boolean(searchTerm);

  return (
    <main className="accept-app">
      <div className="accept-app__container">

        {/* HEADER */}
        <header className="accept-page-header">
          <div className="accept-page-heading">
            <div className="accept-page-eyebrow">
              <span />
              ADMISSIONS MANAGEMENT
            </div>

            <h1>Acceptance</h1>

            <p>
              Monitor admission responses, payment status and
              candidate acceptance activity from one
              centralized workspace.
            </p>
          </div>

          <button
            type="button"
            className="accept-refresh"
            onClick={getApplicants}
            disabled={loading}
          >
            <RefreshOutlined
              className={
                loading
                  ? "accept-refresh--spinning"
                  : ""
              }
            />
            Refresh records
          </button>
        </header>

        {/* METRICS */}
        <section className="accept-metrics">
          <MetricCard
            icon={<GroupsOutlined />}
            value={counts.count}
            label="Admitted applicants"
            description="Candidates currently in acceptance"
            variant="blue"
          />

          <MetricCard
            icon={<HourglassTopOutlined />}
            value={counts.pendings}
            label="Pending"
            description="Awaiting candidate response"
            variant="yellow"
          />

          <MetricCard
            icon={<CheckCircleOutlined />}
            value={counts.accepteds}
            label="Accepted"
            description="Candidates who accepted admission"
            variant="green"
          />

          <MetricCard
            icon={<CancelOutlined />}
            value={counts.rejecteds}
            label="Rejected"
            description="Candidates who declined admission"
            variant="red"
          />
        </section>

        {/* FILTER WORKSPACE */}
        <section className="accept-filter-card">
          <div className="accept-filter-header">
            <div className="accept-filter-title">
              <div className="accept-filter-icon">
                <FilterAltOutlined />
              </div>

              <div>
                <span>ACCEPTANCE FILTERS</span>
                <h2>Refine records</h2>
              </div>
            </div>

            {hasFilters && (
              <button
                type="button"
                className="accept-clear"
                onClick={clearFilters}
              >
                <CloseOutlined />
                Clear all
              </button>
            )}
          </div>

          <div className="accept-filter-grid">
            <label className="accept-field">
              <span>Application month</span>

              <div className="accept-select">
                <CalendarMonthOutlined />

                <select
                  value={month}
                  onChange={(event) =>
                    setMonth(event.target.value)
                  }
                >
                  <option value="">
                    All months
                  </option>

                  <option value="all">All</option>

                  {MONTHS.map((item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <div className="accept-filter-action">
              <button
                type="button"
                onClick={
                  month === "all"
                    ? getApplicants
                    : getByMonths
                }
                disabled={loading}
              >
                <SearchOutlined />
                Apply filter
              </button>
            </div>
          </div>
        </section>

        {/* TOOLBAR */}
        <section className="accept-toolbar">
          <div className="accept-search">
            <SearchOutlined />

            <input
              type="search"
              value={searchTerm}
              placeholder="Search applicant, application number, invoice or reference..."
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
              >
                <CloseOutlined />
              </button>
            )}
          </div>

          <div className="accept-toolbar__right">
            <div className="accept-view-switcher">
              <button
                type="button"
                className={
                  changeview === "table"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setChangeView("table")
                }
              >
                <TableRowsOutlined />
                <span>Table</span>
              </button>

              <button
                type="button"
                className={
                  changeview === "grid"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setChangeView("grid")
                }
              >
                <AppsOutlined />
                <span>Grid</span>
              </button>
            </div>
          </div>
        </section>

        {/* CONTENT HEADER */}
        <div className="accept-content-heading">
          <div>
            <span>ACCEPTANCE RECORDS</span>

            <strong>
              {visibleApplicants.length}{" "}
              {visibleApplicants.length === 1
                ? "candidate"
                : "candidates"}
            </strong>
          </div>

          {hasFilters && (
            <div className="accept-active-filter">
              <FilterAltOutlined />
              Filters active
            </div>
          )}
        </div>

        {/* DATA */}
        {visibleApplicants.length === 0 ? (
          <EmptyState
            filtered={hasFilters}
            clearFilters={clearFilters}
          />
        ) : changeview === "grid" ? (
          <CardView applicants={visibleApplicants} />
        ) : (
          <TableView applicants={visibleApplicants} />
        )}

        {/* LOADING */}
        {loading && (
          <div className="accept-loading">
            <div className="accept-loading__spinner" />

            <div>
              <strong>Updating acceptance records</strong>
              <span>
                Fetching the latest admission activity...
              </span>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}